import axios from 'axios';
import Logger from 'logger/Logger';
import { customGraphQLHeaders } from 'utils/AuthSession';
import Helper, { EndpointType } from 'utils/Helper';

const logger = Logger.getLogger('CsrfToken');

export const CSRF_HEADER = 'x-csrf-token';
export const CSRF_TOKEN_KEY = 'CSRF_TOKEN';

// Track ongoing CSRF token refresh to prevent concurrent refreshes
// and allow other operations to wait for the refresh to complete
let csrfRefreshPromise: Promise<void> | null = null;

/**
 * Wait for any ongoing CSRF token refresh to complete.
 * If no refresh is in progress, resolves immediately.
 * This should be called before making API requests to ensure the CSRF token is fresh.
 */
export const waitForCsrfRefresh = async (): Promise<void> => {
  if (csrfRefreshPromise) {
    logger.debug('Waiting for ongoing CSRF token refresh to complete');
    await csrfRefreshPromise;
  }
};

/**
 * Check if a CSRF token refresh is currently in progress.
 */
export const isCsrfRefreshing = (): boolean => {
  return csrfRefreshPromise !== null;
};

export const SaveCsrfToken = async (): Promise<void> => {
  // If already refreshing, return the existing promise to avoid duplicate requests
  if (csrfRefreshPromise) {
    logger.debug('CSRF refresh already in progress, waiting for it to complete');
    return csrfRefreshPromise;
  }

  // Create the refresh promise
  csrfRefreshPromise = (async () => {
    const headers = await customGraphQLHeaders();
    try {
      logger.debug('Fetching new CSRF token');
      const response = await axios.get(Helper.getEndpoint(EndpointType.Csrf), { headers });
      logger.debug('Got CSRF Response', { response });
      sessionStorage.setItem(CSRF_TOKEN_KEY, response.headers[CSRF_HEADER]);
      logger.info('CSRF token refreshed successfully');
    } catch (err) {
      logger.error('failed to get csrf token', err);
      throw err; // Re-throw so callers know it failed
    } finally {
      // Clear the promise so future calls will trigger a new refresh
      csrfRefreshPromise = null;
    }
  })();

  return csrfRefreshPromise;
};

export const GetCsrfToken = (): string | null => {
  return sessionStorage.getItem(CSRF_TOKEN_KEY);
};

export const RemoveCsrfToken = (): void => {
  sessionStorage.removeItem(CSRF_TOKEN_KEY);
};
